/**
 * Helpers for `<input type="date">` values.
 *
 * Both directions are hand-rolled rather than using `new Date(string)` /
 * `toISOString()`, because both of those are silently wrong here:
 *
 *  - `new Date('2026-05-01')` parses as **UTC** midnight. Anywhere west of
 *    Greenwich that is the previous day in local time, so a seed typed as May 1
 *    reads back as Apr 30 and every neglect figure is off by one.
 *  - `new Date(2026, 1, 31)` does not throw for a nonexistent date — it rolls
 *    over to March 3rd. A fat-fingered seed must be rejected, not relocated.
 *
 * That matters because these values seed `lastWorkedAt`, which the
 * pre-registration (§8) calls the single most manipulable input in the decay
 * experiment. A seed that is quietly wrong is worse than one that is refused.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Parse a `YYYY-MM-DD` value into local midnight of that calendar day.
 * Returns undefined for empty, malformed, or nonexistent dates — undefined
 * means "no seed", which lets scoring fall back to `createdAt`.
 */
export function parseDateInputValue(value: string): Date | undefined {
  const match = YMD.exec(value.trim())
  if (!match) return undefined

  const [, y, m, d] = match
  const year = Number(y)
  const monthIndex = Number(m) - 1
  const day = Number(d)

  const date = new Date(year, monthIndex, day)

  // Reject anything the Date constructor silently relocated (Feb 31 → Mar 3,
  // month 13 → next January). If it survives the round trip it was a real date.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return undefined
  }

  return date
}

/** Format a Date as `YYYY-MM-DD` in local time (the inverse of the above). */
export function formatDateInputValue(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
